/**
 * Utility functions for detecting and handling embedded mode.
 *
 * When the app is loaded inside an iframe from the new i4cast-web v2 app,
 * it receives a query parameter ?embedded=true to indicate it should hide
 * navigation elements (header, sidebar, footer) and adjust its layout.
 */

const EMBEDDED_MODE_KEY = 'i4cast_embedded_mode';

/**
 * Checks if the application is running in embedded mode.
 * Uses multiple detection strategies to handle SPA navigation:
 * 1. URL parameter ?embedded=true (initial load)
 * 2. sessionStorage cache (persists during session/navigation)
 * 3. iframe detection (fallback)
 *
 * @returns true if running in embedded mode, false otherwise
 *
 * @example
 * // Normal mode: https://i4cast.i4sea.com/forecast-charts
 * isEmbeddedMode() // false
 *
 * // Embedded mode: https://i4cast.i4sea.com/forecast-charts?embedded=true
 * isEmbeddedMode() // true
 *
 * // After navigation to /insight-manager (no query param)
 * isEmbeddedMode() // true (uses sessionStorage cache or iframe detection)
 */
export function isEmbeddedMode(): boolean {
  // Strategy 1: Check URL parameter (initial load)
  const params = new URLSearchParams(window.location.search);
  const urlParam = params.get('embedded') === 'true';

  // If URL param is present, cache it and return true
  if (urlParam) {
    try {
      sessionStorage.setItem(EMBEDDED_MODE_KEY, 'true');
    } catch (e) {
      // sessionStorage might not be available in some contexts
    }
    return true;
  }

  // Strategy 2: Check sessionStorage cache (persists during SPA navigation)
  try {
    if (sessionStorage.getItem(EMBEDDED_MODE_KEY) === 'true') {
      return true;
    }
  } catch (e) {
    // sessionStorage might not be available
  }

  // Strategy 3: Check if running inside iframe (fallback)
  // If we're in an iframe, we're probably embedded
  if (isInsideIframe()) {
    try {
      sessionStorage.setItem(EMBEDDED_MODE_KEY, 'true');
    } catch (e) {
      // sessionStorage might not be available
    }
    return true;
  }

  return false;
}

/**
 * Checks if the application is running inside an iframe.
 *
 * @returns true if window is not the top window (inside iframe), false otherwise
 */
export function isInsideIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch (e) {
    // If we can't access window.top due to cross-origin, we're in an iframe
    return true;
  }
}

// ============================================================================
// PostMessage Authentication for Embedded Mode
// ============================================================================

// ============================================================================
// JWT Token Parsing Utilities
// ============================================================================

/**
 * Extracts userId from a Clerk JWT token payload.
 * Clerk maps publicMetadata.internalUserId to the `userId` claim in the JWT.
 *
 * Note: This function only decodes the JWT payload without signature verification.
 * The JWT signature is validated by the backend API on each request. This client-side
 * extraction is only used for populating the MobX store for UI/routing purposes.
 *
 * @param token - JWT token string
 * @returns The userId from the token payload, or undefined if not found
 */
function extractUserIdFromToken(token: string): number | undefined {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return undefined;

    // Decode the payload (second part of JWT)
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));

    // Clerk JWT includes userId directly in the payload (mapped from publicMetadata.internalUserId)
    const rawUserId = payload?.userId;
    const userId = typeof rawUserId === 'string' ? parseInt(rawUserId, 10) : rawUserId;

    if (typeof userId === 'number' && !isNaN(userId) && userId > 0) {
      return userId;
    }
    return undefined;
  } catch (e) {
    console.warn('[EmbeddedAuth] Failed to extract userId from token:', e);
    return undefined;
  }
}

/**
 * Token received from parent app via postMessage.
 * Stored in memory only (not localStorage) for security.
 */
let embeddedToken: string | null = null;

/**
 * Callbacks waiting for the token to be received.
 */
let tokenReadyCallbacks: Array<(token: string) => void> = [];

/**
 * Gets the token received from parent app in embedded mode.
 *
 * @returns The JWT token or null if not yet received
 */
export function getEmbeddedToken(): string | null {
  return embeddedToken;
}

/**
 * Gets the current user's userId by extracting it from the embedded JWT token.
 * This is the preferred way to get userId in embedded mode — avoids localStorage dependency.
 *
 * @returns The userId as a number, or 0 if token is not available or userId is not in the token
 */
export function getEmbeddedUserId(): number {
  if (!embeddedToken) return 0;
  return extractUserIdFromToken(embeddedToken) ?? 0;
}

/**
 * Sets the embedded token (used internally by initEmbeddedAuth).
 * Also exported for testing purposes.
 *
 * @param token - The JWT token to set
 */
export function setEmbeddedToken(token: string | null): void {
  embeddedToken = token;
}

/**
 * Waits for the token to be received from parent app.
 * Returns immediately if token already exists.
 *
 * @returns Promise that resolves with the token
 *
 * @example
 * const token = await waitForEmbeddedToken();
 * // Use token for API requests
 */
export function waitForEmbeddedToken(): Promise<string> {
  return new Promise((resolve) => {
    if (embeddedToken) {
      resolve(embeddedToken);
    } else {
      tokenReadyCallbacks.push(resolve);
    }
  });
}

/**
 * Stored allowed origins for sending messages to parent.
 */
let storedAllowedOrigins: string[] = [];

/**
 * Verified parent origin from successful AUTH_TOKEN message.
 * This is the actual origin of the parent window, captured when we receive a token.
 * Used for subsequent postMessage communication to ensure messages reach the parent.
 */
let verifiedParentOrigin: string | null = null;

/**
 * Request a fresh token from parent app.
 * Useful when the current token has expired (401 response).
 *
 * @param parentOrigin - Optional specific origin to send to.
 *   Priority: 1. provided origin, 2. verified origin from AUTH_TOKEN, 3. configured allowed origin
 */
export function requestTokenFromParent(parentOrigin?: string): void {
  if (!isEmbeddedMode() || !isInsideIframe()) return;

  // Priority: 1. provided origin, 2. verified origin from AUTH_TOKEN, 3. fallback to configured
  const targetOrigin = parentOrigin || verifiedParentOrigin || storedAllowedOrigins[0];
  if (!targetOrigin) {
    console.warn('[EmbeddedAuth] No parent origin available for REQUEST_TOKEN');
    return;
  }

  window.parent.postMessage({ type: 'REQUEST_TOKEN' }, targetOrigin);
  console.log('[EmbeddedAuth] Sent REQUEST_TOKEN to parent at:', targetOrigin);
}

/**
 * Clears the current token and requests a fresh one from parent app.
 * Waits for the new token to arrive before resolving.
 *
 * @param timeoutMs - Timeout in milliseconds to wait for new token (default: 10000)
 * @returns Promise that resolves with the new token or rejects on timeout
 */
export async function refreshEmbeddedToken(timeoutMs: number = 10000): Promise<string> {
  if (!isEmbeddedMode() || !isInsideIframe()) {
    return Promise.reject(new Error('Not in embedded mode'));
  }

  // Clear current token so waitForEmbeddedToken will wait for new one
  embeddedToken = null;

  // Request fresh token from parent
  requestTokenFromParent();

  // Wait for new token with timeout
  return Promise.race([
    waitForEmbeddedToken(),
    new Promise<string>((_, reject) =>
      setTimeout(() => reject(new Error('Token refresh timeout')), timeoutMs)
    ),
  ]);
}

/**
 * Asks the parent app (Aurora) to navigate to a native route.
 * No-op when running standalone (not embedded).
 *
 * The Aurora parent validates both origin and path against a small allowlist
 * before performing the navigation — see LegacyIframe.tsx.
 *
 * @param path - Absolute path within Aurora (e.g. '/event-manager')
 */
export function navigateParent(path: string): void {
  if (!isEmbeddedMode() || !isInsideIframe()) return;

  const targetOrigin = verifiedParentOrigin || storedAllowedOrigins[0];
  if (!targetOrigin) {
    console.warn('[EmbeddedAuth] No parent origin available for NAVIGATE_PARENT');
    return;
  }

  window.parent.postMessage({ type: 'NAVIGATE_PARENT', path }, targetOrigin);
  console.log('[EmbeddedAuth] Sent NAVIGATE_PARENT to parent:', path);
}

/**
 * Initialize postMessage listener for embedded mode authentication.
 * Should be called once at app startup.
 *
 * @param allowedOrigins - List of allowed origins that can send tokens
 * @returns Cleanup function to remove the event listener
 *
 * @example
 * if (isEmbeddedMode()) {
 *   const cleanup = initEmbeddedAuth([
 *     'https://i4cast-frontend-demo.i4sea.com.br',
 *     'http://localhost:3000',
 *   ]);
 *   // Call cleanup() when no longer needed
 * }
 */
export function initEmbeddedAuth(allowedOrigins: string[]): () => void {
  if (!isEmbeddedMode()) return () => { };

  // Store allowed origins for use in requestTokenFromParent
  storedAllowedOrigins = allowedOrigins;

  const handleMessage = (event: MessageEvent) => {
    // Security: Validate origin
    if (!allowedOrigins.includes(event.origin)) {
      console.warn('[EmbeddedAuth] Received message from unauthorized origin:', event.origin);
      return;
    }

    // Handle AUTH_TOKEN message from parent
    if (event.data?.type === 'AUTH_TOKEN' && event.data.token) {
      embeddedToken = event.data.token;

      // Store the verified parent origin for future communication (e.g., REQUEST_TOKEN)
      verifiedParentOrigin = event.origin;

      // Store companyId in localStorage if provided by parent app
      if ('companyId' in event.data) {
        try {
          localStorage.setItem('companyId', String(event.data.companyId));
        } catch (e) {
          console.warn('[EmbeddedAuth] Failed to store companyId in localStorage:', e);
        }
      }

      // Store permission flags in localStorage if provided by parent app
      try {
        if (event.data.admin === true) {
          localStorage.setItem('@admin_user', String(event.data.admin));
        }
        if (event.data.readOnly === true) {
          localStorage.setItem('@read_only_user', String(event.data.readOnly));
        }
      } catch (e) {
        console.warn('[EmbeddedAuth] Failed to store permission flags in localStorage:', e);
      }

      // Dispatch event so the store picks up userId BEFORE tokenReadyCallbacks run.
      // This ensures authStore.userId is populated before any async operations
      // triggered by waitForEmbeddedToken() resolve.
      const userId = extractUserIdFromToken(embeddedToken!);
      if (userId) {
        window.dispatchEvent(new CustomEvent('embedded-auth-token', { detail: { userId } }));
      }

      // Notify all waiting callbacks (userId is already in the store at this point)
      tokenReadyCallbacks.forEach((cb) => cb(embeddedToken!));
      tokenReadyCallbacks = [];

      console.log('[EmbeddedAuth] Received auth token from parent app at origin:', event.origin);
    }

    // Handle LOGOUT message from parent
    if (event.data?.type === 'LOGOUT') {
      // Security: verify the message comes from the parent window
      if (event.source !== window.parent) {
        console.warn('[EmbeddedAuth] LOGOUT message rejected: not from parent window');
        return;
      }

      console.log('[EmbeddedAuth] Received LOGOUT from parent app');

      // Clear embedded token and verified origin (don't clear tokenReadyCallbacks
      // so any pending waitForEmbeddedToken() promises can resolve on next login)
      embeddedToken = null;
      verifiedParentOrigin = null;

      // Dispatch event for store cleanup
      window.dispatchEvent(new CustomEvent('embedded-logout'));
    }
  };

  window.addEventListener('message', handleMessage);

  // Notify parent that iframe is ready to receive token.
  //
  // ⚠️ Anuncia para TODAS as origens autorizadas, uma a uma, e nunca para `'*'`.
  //
  // 📖 Mandar so para `allowedOrigins[0]` funcionava por acidente: enquanto havia um
  // unico pai de fato, ele era o primeiro da lista. Com mais de um deployment
  // autorizado o primeiro deixa de ser o certo -- e `postMessage` com origem-alvo
  // diferente da origem real do pai e DESCARTADO PELO NAVEGADOR EM SILENCIO, sem erro
  // e sem log. O pai entao nunca manda o `AUTH_TOKEN`, porque ele so responde a este
  // anuncio, e a tela embutida fica sem sessao sem nada explicar o porque. Foi o que
  // apareceu ao embutir este app no aurora de `i4cast-demo-event-map.netlify.app`
  // (workspace#385, 09/09/2026).
  //
  // ⚠️ O laco NAO afrouxa a seguranca, e e por isso que ele resolve: cada chamada
  // nomeia UMA origem, e o navegador entrega so a que casa com o pai real -- as outras
  // morrem no cliente sem sair. O que seria inseguro e `'*'`, que entregaria o anuncio
  // a qualquer pai, inclusive um que embutiu este app sem autorizacao.
  //
  // 📖 A origem verdadeira do pai nao e legivel daqui (`window.parent.origin` e
  // cross-origin), e ela chega depois: o primeiro `AUTH_TOKEN` a registra em
  // `verifiedParentOrigin`, e dali em diante o `REQUEST_TOKEN` fala direto com ela.
  if (allowedOrigins.length > 0) {
    allowedOrigins.forEach((origin) => {
      window.parent.postMessage({ type: 'IFRAME_READY' }, origin);
    });
    console.log(`[EmbeddedAuth] Sent IFRAME_READY to ${allowedOrigins.length} allowed origin(s)`);
  } else {
    console.warn('[EmbeddedAuth] No allowed origins configured, cannot send IFRAME_READY');
  }

  // Return cleanup function
  return () => {
    window.removeEventListener('message', handleMessage);
    storedAllowedOrigins = [];
    console.log('[EmbeddedAuth] Cleaned up message listener');
  };
}
